/**
 * Copia del mapa de plantillas del Cloudflare Worker.
 *
 * Fuente de verdad: `cloudflare-worker-anaafernandez/worker.js` → `COUNTRY_TEMPLATE_MAP` + `DEFAULT_CONFIG`.
 * El Worker suele mandar `BaseTemplate` para la home. En el front, visitantes que no entran en el
 * criterio Argentina (`usesSpanishDefaultForSite`) sustituyen esa familia por la plantilla configurada
 * en admin (`site_settings.international_home_template`, default `DualCTAVideoLanding`).
 *
 * Tras cambios en el Worker, actualizar este archivo y desplegar el front.
 */

import type { ServerTemplateConfig } from "@/lib/serverTemplateDetection";

export type WorkerTemplateRow = {
  template: string;
  bioLayout: null;
  language: string;
};

/** Igual que `DEFAULT_CONFIG` en worker.js */
export const WORKER_DEFAULT_TEMPLATE: WorkerTemplateRow = {
  template: "DualCTAVideoLanding",
  bioLayout: null,
  language: "en-US",
};

/**
 * Igual que `COUNTRY_TEMPLATE_MAP` + `getARConfig` / `getUSConfig` en worker.js (sin rutas especiales como /sorprendeme).
 */
export const WORKER_COUNTRY_TEMPLATE_MAP: Record<string, WorkerTemplateRow> = {
  AR: { template: "BaseTemplate", bioLayout: null, language: "es-AR" },

  EC: { template: "DualCTAVideoLanding", bioLayout: null, language: "es-EC" },
  MX: { template: "DualCTAVideoLanding", bioLayout: null, language: "es-MX" },
  CL: { template: "DualCTAVideoLanding", bioLayout: null, language: "es-CL" },
  CO: { template: "DualCTAVideoLanding", bioLayout: null, language: "es-CO" },
  PE: { template: "DualCTAVideoLanding", bioLayout: null, language: "es-PE" },
  UY: { template: "DualCTAVideoLanding", bioLayout: null, language: "es-UY" },
  VE: { template: "DualCTAVideoLanding", bioLayout: null, language: "es-VE" },
  BO: { template: "DualCTAVideoLanding", bioLayout: null, language: "es-BO" },
  CR: { template: "DualCTAVideoLanding", bioLayout: null, language: "es-CR" },
  PA: { template: "DualCTAVideoLanding", bioLayout: null, language: "es-PA" },
  DO: { template: "DualCTAVideoLanding", bioLayout: null, language: "es-DO" },
  GT: { template: "DualCTAVideoLanding", bioLayout: null, language: "es-GT" },
  HN: { template: "DualCTAVideoLanding", bioLayout: null, language: "es-HN" },
  NI: { template: "DualCTAVideoLanding", bioLayout: null, language: "es-NI" },
  SV: { template: "DualCTAVideoLanding", bioLayout: null, language: "es-SV" },
  PY: { template: "DualCTAVideoLanding", bioLayout: null, language: "es-PY" },

  BR: { template: "DualCTAVideoLanding", bioLayout: null, language: "pt-BR" },

  ES: { template: "DualCTAVideoLanding", bioLayout: null, language: "es-ES" },

  US: { template: "DualCTAVideoLanding", bioLayout: null, language: "en-US" },
  GB: { template: "DualCTAVideoLanding", bioLayout: null, language: "en-GB" },
  IE: { template: "DualCTAVideoLanding", bioLayout: null, language: "en-IE" },
  AU: { template: "DualCTAVideoLanding", bioLayout: null, language: "en-AU" },

  DE: { template: "DualCTAVideoLanding", bioLayout: null, language: "de-DE" },
  FR: { template: "DualCTAVideoLanding", bioLayout: null, language: "fr-FR" },
  IT: { template: "DualCTAVideoLanding", bioLayout: null, language: "it-IT" },
  PT: { template: "DualCTAVideoLanding", bioLayout: null, language: "pt-PT" },
  NL: { template: "DualCTAVideoLanding", bioLayout: null, language: "nl-NL" },
  BE: { template: "DualCTAVideoLanding", bioLayout: null, language: "fr-BE" },
  CH: { template: "DualCTAVideoLanding", bioLayout: null, language: "de-CH" },
  AT: { template: "DualCTAVideoLanding", bioLayout: null, language: "de-AT" },
  GR: { template: "DualCTAVideoLanding", bioLayout: null, language: "el-GR" },
  DK: { template: "DualCTAVideoLanding", bioLayout: null, language: "da-DK" },
  SE: { template: "DualCTAVideoLanding", bioLayout: null, language: "sv-SE" },
  NO: { template: "DualCTAVideoLanding", bioLayout: null, language: "nb-NO" },
  FI: { template: "DualCTAVideoLanding", bioLayout: null, language: "fi-FI" },
  PL: { template: "DualCTAVideoLanding", bioLayout: null, language: "pl-PL" },
  CZ: { template: "DualCTAVideoLanding", bioLayout: null, language: "cs-CZ" },
  HU: { template: "DualCTAVideoLanding", bioLayout: null, language: "hu-HU" },
  RO: { template: "DualCTAVideoLanding", bioLayout: null, language: "ro-RO" },
  BG: { template: "DualCTAVideoLanding", bioLayout: null, language: "bg-BG" },
  HR: { template: "DualCTAVideoLanding", bioLayout: null, language: "hr-HR" },
  SI: { template: "DualCTAVideoLanding", bioLayout: null, language: "sl-SI" },
  SK: { template: "DualCTAVideoLanding", bioLayout: null, language: "sk-SK" },
  LT: { template: "DualCTAVideoLanding", bioLayout: null, language: "lt-LT" },
  LV: { template: "DualCTAVideoLanding", bioLayout: null, language: "lv-LV" },
  EE: { template: "DualCTAVideoLanding", bioLayout: null, language: "et-EE" },
  LU: { template: "DualCTAVideoLanding", bioLayout: null, language: "fr-LU" },
  MT: { template: "DualCTAVideoLanding", bioLayout: null, language: "mt-MT" },
  CY: { template: "DualCTAVideoLanding", bioLayout: null, language: "el-CY" },
  IS: { template: "DualCTAVideoLanding", bioLayout: null, language: "is-IS" },

  AE: { template: "DualCTAVideoLanding", bioLayout: null, language: "ar-AE" },
  SA: { template: "DualCTAVideoLanding", bioLayout: null, language: "ar-SA" },
  EG: { template: "DualCTAVideoLanding", bioLayout: null, language: "ar-EG" },

  ID: { template: "DualCTAVideoLanding", bioLayout: null, language: "id-ID" },
};

/**
 * Configuración alineada con el Worker para overrides `?country=XX` (debug) y tests.
 */
export function getWorkerTemplateConfigForCountry(country: string): ServerTemplateConfig {
  const code = country.toUpperCase().trim().slice(0, 2);
  const row = WORKER_COUNTRY_TEMPLATE_MAP[code] ?? WORKER_DEFAULT_TEMPLATE;
  return {
    country: code,
    templateName: row.template,
    bioLayout: row.bioLayout,
    language: row.language,
    province: null,
    cfCountry: code,
    timezone: "",
    city: "",
    detectionMethod: "manual_override_worker_map",
  };
}
